import {
  Breadcrumbs,
  Button,
  Inline,
  Stack,
  Typography,
  useModal,
} from '@omnipresentgroup/design-system';
import { ContractorsList } from '@omnipresentgroup/features';
import type { Contractor } from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { environment } from '../../../../environment';
import { ADD_HIRE_PATH, CONTRACTOR_DETAILS_PATH } from '../../../constants';
import { ContractorLiabilityShieldModal } from '../../../features/contractor-liability-shield';
import { useManagerUser } from '../../../hooks';
import { ContractorActionCell } from './components';

export const CONTRACTOR_LIST_PAGE_TITLE = 'Contractors';

export const ContractorsListPage = () => {
  const navigate = useNavigate();

  const { modalProps } = useModal({});
  const [modalOpen, setModalOpen] = useState(false);

  const { companyId } = useManagerUser();

  const columnHelper = useMemo(() => createColumnHelper<Contractor>(), []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<Contractor, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <ContractorActionCell
            contractorId={info.row.original.id}
            contractorState={info.row.original.state}
          />
        ),
        header: () => '',
        size: 45,
        meta: {
          onClickStopPropagation: true,
        },
      }),
    ],
    [columnHelper],
  );

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'contractors',
              title: CONTRACTOR_LIST_PAGE_TITLE,
              active: true,
            },
          ]}
        />
        <Inline minH={40} align="center" justify="space-between">
          <Typography as="h3" hideParagraphSpacing size="24" weight="medium">
            {CONTRACTOR_LIST_PAGE_TITLE}
          </Typography>
          <Inline>
            <Button
              id="omniprotect"
              variant="secondary"
              onClick={() => setModalOpen(true)}
            >
              Omniprotect
            </Button>
            <Button
              id="add-hire"
              onClick={() => {
                window.location.href = `${
                  environment.apps.OMNIPLATFORM
                }${ADD_HIRE_PATH(companyId)}?type=contractor`;
              }}
            >
              Add hire
            </Button>
          </Inline>
        </Inline>
      </Stack>
      <ContractorsList
        columnVisibility={{
          contractorName: true,
          jobRole: true,
          country: true,
          startDate: true,
          endDate: true,
          pay: true,
          state: true,
          liabilityShield: true,
        }}
        onRowClick={(row) => {
          navigate(CONTRACTOR_DETAILS_PATH(row.id));
        }}
        emptyStateProps={{
          id: 'contractors-list-empty-state',
          title: 'No contractor has been added',
          description: 'Click Add hire button to add your first contractor',
        }}
        columnsOverride={columns}
      />
      <ContractorLiabilityShieldModal
        modalProps={{
          ...modalProps,
          open: modalOpen,
          onOpenChange: setModalOpen,
        }}
        handleClose={() => setModalOpen(false)}
      />
    </>
  );
};
